import { motion } from "framer-motion";

const MERIDIANS = 9;
const NODES = [
  { rotY: 24, rotX: 8 },
  { rotY: -46, rotX: -22 },
  { rotY: 88, rotX: 4 },
  { rotY: 150, rotX: 26 },
  { rotY: -120, rotX: -12 },
  { rotY: 200, rotX: 16 },
];

/**
 * Pure-CSS/SVG-free rotating digital wireframe globe.
 * Meridian great-circles + latitudes form the sphere; glowing surface nodes
 * orbit with rotation; a tilted orbit ring carries a satellite.
 */
export default function DigitalGlobe({ className = "" }) {
  return (
    <div className={`relative ${className}`} style={{ perspective: 1000 }} data-testid="digital-globe">
      {/* ambient glow */}
      <div
        className="absolute inset-0 rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle at 50% 42%, rgba(0,229,255,0.22), transparent 68%)" }}
      />

      {/* tilted orbit ring + satellite */}
      <div className="absolute inset-[-16%]" style={{ transformStyle: "preserve-3d" }}>
        <div className="absolute inset-0" style={{ transform: "rotateX(72deg)" }}>
          <motion.div
            className="relative h-full w-full rounded-full border border-white/12"
            animate={{ rotate: 360 }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          >
            <span
              className="absolute left-1/2 top-0 h-2.5 w-2.5 -translate-x-1/2 rounded-full bg-white"
              style={{ boxShadow: "0 0 12px #fff, 0 0 24px rgba(0,229,255,0.7)" }}
            />
          </motion.div>
        </div>
      </div>

      {/* rotating globe */}
      <motion.div
        className="relative h-full w-full"
        style={{ transformStyle: "preserve-3d", rotateX: -14 }}
        animate={{ rotateY: 360 }}
        transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
      >
        {/* core sphere */}
        <div
          className="absolute inset-[8%] rounded-full"
          style={{
            background:
              "radial-gradient(circle at 35% 28%, rgba(10,132,255,0.28), rgba(2,6,23,0.92) 68%)",
            boxShadow: "inset 0 0 70px rgba(0,229,255,0.18)",
          }}
        />

        {/* meridians (longitude great-circles) */}
        {Array.from({ length: MERIDIANS }).map((_, i) => (
          <div
            key={`m${i}`}
            className="absolute inset-[8%] rounded-full border border-arjuna-teal/20"
            style={{ transform: `rotateY(${(i * 180) / MERIDIANS}deg)` }}
          />
        ))}

        {/* latitudes */}
        <div className="absolute inset-[8%] rounded-full border border-arjuna-teal/30" style={{ transform: "rotateX(90deg)" }} />
        <div className="absolute inset-[8%] rounded-full border border-arjuna-teal/15" style={{ transform: "rotateX(90deg) translateZ(46px) scale(0.82)" }} />
        <div className="absolute inset-[8%] rounded-full border border-arjuna-teal/15" style={{ transform: "rotateX(90deg) translateZ(-46px) scale(0.82)" }} />

        {/* surface nodes */}
        {NODES.map((n, i) => (
          <div
            key={`n${i}`}
            className="absolute left-1/2 top-1/2"
            style={{ transform: `rotateY(${n.rotY}deg) rotateX(${n.rotX}deg) translateZ(120px)` }}
          >
            <span
              className="block h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{ background: i % 2 ? "#10B981" : "#00E5FF", boxShadow: "0 0 10px currentColor" }}
            />
          </div>
        ))}
      </motion.div>

      {/* rim highlight */}
      <div
        className="pointer-events-none absolute inset-[8%] rounded-full"
        style={{ boxShadow: "inset 0 0 1px rgba(0,229,255,0.6), 0 0 40px rgba(0,229,255,0.12)" }}
      />

      {/* INDIA marker. pings each time the satellite completes a pass (8s cycle) */}
      <div className="pointer-events-none absolute" style={{ left: "56%", top: "45%" }} data-testid="india-ping">
        {/* radar ping ripples */}
        {[0, 1].map((k) => (
          <motion.span
            key={k}
            className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full border border-arjuna-teal"
            style={{ width: 18, height: 18 }}
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: [0.4, 3.4], opacity: [0.9, 0] }}
            transition={{
              duration: 1.8,
              repeat: Infinity,
              repeatDelay: 8 - 1.8,
              ease: "easeOut",
              delay: k * 0.4,
            }}
          />
        ))}
        {/* India dot with a synced brightening flash */}
        <motion.span
          className="absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-arjuna-teal"
          animate={{ boxShadow: [
            "0 0 6px #00E5FF",
            "0 0 22px #00E5FF, 0 0 40px rgba(0,229,255,0.8)",
            "0 0 6px #00E5FF",
          ] }}
          transition={{ duration: 1.6, repeat: Infinity, repeatDelay: 8 - 1.6, ease: "easeOut" }}
        />
        <span className="absolute left-3 -top-2 font-mono text-[9px] tracking-[0.2em] text-arjuna-teal">
          INDIA
        </span>
      </div>
    </div>
  );
}
